export interface APIError {
  status: number;
  message: string;
  /** Corpo bruto da resposta, só para log: quem decide lê `code`, `details` e `message`. */
  originalError?: string;
  code?: string;
  /** `details` do envelope: mapa por campo ou lista de `{ field, message }`. */
  details?: unknown;
}

export class APIErrorException extends Error implements APIError {
  public status: number;
  public originalError?: string;
  /** Cabeçalhos da resposta de erro, com os nomes em minúsculas (ex.: `retry-after`). */
  public headers?: Record<string, string>;
  /** Código de negócio do envelope (ex.: `SIMULACAO_NAO_ATIVA`), lido uma vez no `http-client`. */
  public code?: string;
  /** `details` do envelope (mapa ou lista); `undefined` quando o erro não traz. */
  public details?: unknown;

  constructor(
    status: number,
    message: string,
    originalError?: string,
    headers?: Record<string, string>,
    code?: string,
    details?: unknown,
  ) {
    super(message);
    this.name = "APIErrorException";
    this.status = status;
    this.originalError = originalError;
    this.headers = headers;
    this.code = code;
    this.details = details;
  }
}

export class NetworkErrorException extends Error {
  constructor(message = "Erro de rede") {
    super(message);
    this.name = "NetworkErrorException";
  }
}

export class TimeoutErrorException extends Error {
  constructor(message = "Timeout da operação") {
    super(message);
    this.name = "TimeoutErrorException";
  }
}

export function isAPIError(error: unknown): error is APIError {
  return (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    "message" in error &&
    typeof (error as Record<string, unknown>).status === "number"
  );
}

export function isNetworkError(error: unknown): error is NetworkErrorException {
  return error instanceof NetworkErrorException;
}

export function isTimeoutError(error: unknown): error is TimeoutErrorException {
  return error instanceof TimeoutErrorException;
}

export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (isAPIError(error)) return error.message;
  return String(error);
}

export const RATE_LIMIT_MESSAGE =
  "Muitas requisições em sequência. Aguarde alguns segundos e tente novamente.";

// O axios devolve esta mensagem técnica quando o corpo da resposta não traz `message`.
const MENSAGEM_TECNICA = /^Request failed with status code \d+$/;

/**
 * Mensagem de uma consulta que falhou, para o estado de erro da tela: a do servidor quando é
 * legível; sem ela (vazia ou o texto técnico do axios), `RATE_LIMIT_MESSAGE` no 429 e `fallback`
 * nos demais.
 */
export function getLoadErrorMessage(error: unknown, fallback: string): string {
  const message = getErrorMessage(error).trim();
  if (message && !MENSAGEM_TECNICA.test(message)) return message;
  return isAPIError(error) && error.status === 429 ? RATE_LIMIT_MESSAGE : fallback;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : undefined;
}

function asDetails(value: unknown): unknown {
  return typeof value === "object" && value !== null ? value : undefined;
}

function asText(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

/**
 * Lê o corpo de erro do bff, uma vez só (o `http-client` chama). Formatos, nesta ordem:
 * corpo do core embrulhado em `details.response`; `error: { code, message, details }` do core
 * repassado; `error` como texto com o código (bff, ou `requireClaim` com `code` no topo).
 */
export function readServiceErrorEnvelope(body: Record<string, unknown> | undefined): {
  message?: string;
  code?: string;
  details?: unknown;
} {
  if (!body) return {};

  const core = asRecord(asRecord(body.details)?.response) ?? body;
  const error = asRecord(core.error);
  return {
    message: asText(core.message) ?? asText(error?.message) ?? asText(body.message),
    code: asText(error?.code) ?? asText(core.code) ?? asText(core.error),
    details: asDetails(error?.details) ?? asDetails(core.details),
  };
}

/** Mensagem, código de negócio (em maiúsculas) e `details` que o `http-client` pôs na exceção. */
export function readServiceError(error: APIError): {
  message: string;
  code: string;
  details: Record<string, unknown>;
} {
  return {
    message: asText(error.message) ?? "",
    code: (asText(error.code) ?? "").toUpperCase(),
    details: Array.isArray(error.details) ? {} : (asRecord(error.details) ?? {}),
  };
}

export function getErrorDetails(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    return { name: error.name, message: error.message, stack: error.stack };
  }
  if (isAPIError(error)) {
    return { status: error.status, message: error.message, originalError: error.originalError };
  }
  return { value: error, type: typeof error };
}
